/**
 * The product mark's geometry and palette — ONE source for the three things that draw it:
 *
 *   1. the React `Logo` in brand.tsx, which maps `MARK_ELEMENTS` to JSX;
 *   2. `markSvg()` below, a string renderer over the same list, which
 *      `infra/render-product-mark.mjs` uses to render the lockup the mail carries as a PNG part
 *      (`packages/core/src/notifications/product-mark-png.ts`);
 *   3. the favicon in `apps/web/index.html`, which is the small form written out by hand in these
 *      same coordinates so the two can be diffed by eye.
 *
 * Plain TypeScript: no JSX, no React, and only erasable syntax (type annotations, `as const`, no
 * enums), so Node can import this file directly with its built-in type stripping — which is how
 * the render script reads it without a build step. Keep it that way.
 *
 * ## The coordinates
 *
 * Every number is in the 512 px master's pixel space — `(140, 100)` here is `(140, 100)` in
 * `FlowZa_Sign_Icon_512x512.png` of the approved logo pack (ADR 0089 §1) — so the drawing can be
 * checked against the reference with a ruler. `BOX` crops the artwork with a margin the plate can
 * wear. The trace was made by rendering candidates at 512 px beside the reference and correcting
 * what differed; the first candidate's stem was 60 wide under a 60-deep arm and read as a heavier
 * letter than the artwork's, which is why the F is 46 under 50.
 */

/** The mark's own palette, read off the approved artwork. Fixed: a logo is the same object on every ground. */
export const MARK = {
  /** The F's ramp, arm to foot. `--color-indigo-*` in tokens.css is drawn from the same blue. */
  fArm: '#3d8cf7',
  fUpper: '#2b5af0',
  fLower: '#2540e0',
  fFoot: '#1a1cc2',
  /** The swoosh and the petal it becomes: sky blue, lighter toward the tip. */
  flow: '#46aef6',
  flowLit: '#7dcefa',
  /** The pen's barrel, lit side to shaded side, and the nib below the seam. */
  penLit: '#3ac46e',
  pen: '#159540',
  nib: '#12773a',
  tip: '#0b4a25',
  /** The signature under the nib — the pen's own green, a shade brighter than the barrel's shade. */
  signature: '#1f9a4b',
  /** The plate the accent panel forces under the mark; the theme decides it everywhere else. */
  plate: '#ffffff',
} as const

/**
 * The viewBox: the artwork with 32 units either side and 28 above and below. Landscape, because
 * the mark is — 200 units of F beside 257 of pen and signature.
 */
export const BOX = { x: 108, y: 72, width: 321, height: 256, radius: 52 } as const

/** Height-to-width of the mark's box; a `Logo`'s `size` is its HEIGHT in CSS pixels. */
export const LOGO_ASPECT = BOX.width / BOX.height

/**
 * The SQUARE crop, for the places an icon has to be square: the browser tab, the iOS home screen,
 * the web-app manifest. It takes the F and the pen and drops the signature's tail, because a
 * landscape lockup letterboxed into a square is mostly empty and the letter ends up tiny.
 *
 * One constant rather than four copies of four numbers: `apps/web/index.html`'s inline SVG
 * favicon and `infra/render-icons.mjs`'s raster set both cite it, so the tab icon and the home
 * screen icon are the same picture and cannot drift.
 */
export const ICON_BOX = { x: 135, y: 76, width: 248, height: 248, radius: 52 } as const

/** Below this the seam, the tip, the second wave and the signature are dropped. */
export const FULL_DETAIL_FROM = 40

export type MarkGradientId = 'f' | 'flow' | 'pen'

export type MarkGradient = {
  readonly id: MarkGradientId
  /** `userSpaceOnUse` coordinates — in the pen's case, the pen group's own rotated space. */
  readonly x1: number
  readonly y1: number
  readonly x2: number
  readonly y2: number
  readonly stops: readonly { readonly offset: number; readonly color: string }[]
}

/**
 * `userSpaceOnUse`, and it matters: the swoosh is drawn over the F, and the default
 * `objectBoundingBox` would give each shape its own copy of a ramp.
 */
export const MARK_GRADIENTS: readonly MarkGradient[] = [
  {
    id: 'f',
    x1: 322,
    y1: 100,
    x2: 150,
    y2: 300,
    stops: [
      { offset: 0, color: MARK.fArm },
      { offset: 0.4, color: MARK.fUpper },
      { offset: 0.72, color: MARK.fLower },
      { offset: 1, color: MARK.fFoot },
    ],
  },
  {
    id: 'flow',
    x1: 150,
    y1: 240,
    x2: 262,
    y2: 180,
    stops: [
      { offset: 0, color: MARK.flow },
      { offset: 1, color: MARK.flowLit },
    ],
  },
  // Across the barrel, in the pen's own space: lit on the left, shaded on the right.
  {
    id: 'pen',
    x1: -18,
    y1: 0,
    x2: 18,
    y2: 0,
    stops: [
      { offset: 0, color: MARK.penLit },
      { offset: 1, color: MARK.pen },
    ],
  },
]

export type MarkFill = string | { readonly gradient: MarkGradientId }

/**
 * One drawn thing. `detail: true` marks the elements only the full form draws (40 px and up);
 * everything else is in both forms.
 */
export type MarkElement =
  | {
      readonly kind: 'fill'
      readonly d: string
      readonly fill: MarkFill
      readonly opacity?: number
      readonly detail?: true
    }
  | {
      readonly kind: 'rect'
      readonly x: number
      readonly y: number
      readonly width: number
      readonly height: number
      readonly fill: string
      readonly opacity?: number
      readonly detail?: true
    }
  | {
      readonly kind: 'stroke'
      readonly d: string
      readonly stroke: string
      readonly width: number
      readonly detail?: true
    }
  | {
      readonly kind: 'group'
      readonly transform: string
      readonly children: readonly MarkElement[]
    }

/**
 * The six elements, in paint order.
 *
 * The F: stem 46 wide, arm 50 deep and 182 long, the outer top-left corner a 50 radius so the
 * stem flows into the arm; the arm's end is rounder above (25) than below (18), as drawn; the
 * foot is a half-round; the inner corner where the arm meets the stem is softened by 8.
 *
 * The swoosh is ONE shape — the band across the stem rising to the right, then out as the petal
 * — so band and petal are one gesture and the band's left edge is the stem's.
 *
 * The pen is drawn upright in its own space with the tip at the origin and turned 36° so it
 * points up and to the right, the way the artwork's does.
 */
export const MARK_ELEMENTS: readonly MarkElement[] = [
  {
    kind: 'fill',
    d: 'M140 150A50 50 0 0 1 190 100H297A25 25 0 0 1 322 125V132A18 18 0 0 1 304 150H194A8 8 0 0 0 186 158V277A23 23 0 0 1 163 300A23 23 0 0 1 140 277Z',
    fill: { gradient: 'f' },
  },
  // The second wave: a faint lift across the stem below the swoosh, so the foot reads as flowing
  // rather than merely darker. Dropped in the small form, where it is a smear.
  {
    kind: 'fill',
    d: 'M140 262C155 257 170 246 186 241V266C170 271 155 282 140 288Z',
    fill: '#ffffff',
    opacity: 0.18,
    detail: true,
  },
  {
    kind: 'fill',
    d: 'M140 205C155 200 170 188 186 183C215 172 250 190 272 212C250 226 215 228 186 225C170 230 155 242 140 247Z',
    fill: { gradient: 'flow' },
  },
  {
    kind: 'group',
    transform: 'translate(266 258) rotate(36)',
    children: [
      { kind: 'fill', d: 'M-18 -44V-160A18 18 0 0 1 18 -160V-44Z', fill: { gradient: 'pen' } },
      { kind: 'fill', d: 'M0 0L-18 -44H18Z', fill: MARK.nib },
      // The seam where the cap meets the barrel, and the near-black tip: full form only.
      {
        kind: 'rect',
        x: -18,
        y: -138,
        width: 36,
        height: 6,
        fill: '#ffffff',
        opacity: 0.5,
        detail: true,
      },
      { kind: 'fill', d: 'M0 0L-4.5 -11H4.5Z', fill: MARK.tip, detail: true },
    ],
  },
  // And what the pen just wrote: a hook under the nib, then the flourish.
  {
    kind: 'stroke',
    d: 'M262 266C256 282 266 292 280 282C290 274 298 268 310 279C322 290 334 288 348 276C360 266 376 262 394 268',
    stroke: MARK.signature,
    width: 6,
    detail: true,
  },
]

/** Whether an element is drawn at this size — the one rule the two forms differ by. */
export function drawn(element: MarkElement, detailed: boolean): boolean {
  return element.kind === 'group' || detailed || element.detail !== true
}

export type MarkSvgOptions = {
  /** Prefix for the gradient ids, which are document-global in inline SVG. */
  readonly idPrefix: string
  /** Height in CSS pixels; the width follows from the box's aspect. */
  readonly height: number
  /** What to crop to. `BOX` (the landscape lockup) by default; `ICON_BOX` for a square icon. */
  readonly box?: {
    readonly x: number
    readonly y: number
    readonly width: number
    readonly height: number
    readonly radius: number
  }
  /** Draw the full form regardless of height. Defaults to `height >= FULL_DETAIL_FROM`. */
  readonly detailed?: boolean
  /** The plate's fill, as a CSS colour; omit for no plate. */
  readonly plate?: string
}

/**
 * The mark as an SVG string — the same drawing `Logo` makes in JSX, for the one consumer that
 * cannot run React: the script that renders the mail's PNG. Kept beside the data so a change to
 * the list is a change to both renderings.
 */
export function markSvg(options: MarkSvgOptions): string {
  const { idPrefix, height } = options
  const box = options.box ?? BOX
  const detailed = options.detailed ?? height >= FULL_DETAIL_FROM
  const width = Math.round((height * box.width) / box.height)
  const fillOf = (fill: MarkFill): string =>
    typeof fill === 'string' ? fill : `url(#${idPrefix}-${fill.gradient})`
  const element = (el: MarkElement): string => {
    if (!drawn(el, detailed)) return ''
    switch (el.kind) {
      case 'fill':
        return `<path d="${el.d}" fill="${fillOf(el.fill)}"${el.opacity === undefined ? '' : ` fill-opacity="${el.opacity}"`}/>`
      case 'rect':
        return `<rect x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}" fill="${el.fill}"${el.opacity === undefined ? '' : ` fill-opacity="${el.opacity}"`}/>`
      case 'stroke':
        return `<path d="${el.d}" fill="none" stroke="${el.stroke}" stroke-width="${el.width}" stroke-linecap="round" stroke-linejoin="round"/>`
      case 'group':
        return `<g transform="${el.transform}">${el.children.map(element).join('')}</g>`
    }
  }
  const gradients = MARK_GRADIENTS.map(
    (g) =>
      `<linearGradient id="${idPrefix}-${g.id}" gradientUnits="userSpaceOnUse" x1="${g.x1}" y1="${g.y1}" x2="${g.x2}" y2="${g.y2}">` +
      g.stops.map((s) => `<stop offset="${s.offset}" stop-color="${s.color}"/>`).join('') +
      `</linearGradient>`,
  )
  const plate =
    options.plate === undefined
      ? ''
      : `<rect x="${box.x}" y="${box.y}" width="${box.width}" height="${box.height}" rx="${box.radius}" fill="${options.plate}"/>`
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${box.x} ${box.y} ${box.width} ${box.height}" aria-hidden="true" focusable="false">` +
    `<defs>${gradients.join('')}</defs>${plate}${MARK_ELEMENTS.map(element).join('')}</svg>`
  )
}
